import React, { useCallback, useContext } from 'react';

import { ChatContainerContext } from '@/components/ChatContainer/ChatContainer';
import { useCreateMessageReactionMutation } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import useLongPress from '@/hooks/useLongPress';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import constants from '@/theme/exportedVariables.module.scss';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useMediaQuery } from '@material-ui/core';
import Linkify from 'linkify-react';
import { useSnackbar } from 'notistack';

import { MessageReaction } from '../../../MessageReactions';
import { MessageCommonProps } from '../../RenderMessage';
import { ReactionPanel } from '../ReactionPanel';
import styles from './MessageContent.module.scss';

export type MessageGroupPosition = 'FIRST' | 'LAST' | 'MID';

// export interface MessageContentProps {
// 	content: string;
// 	isAuthor: boolean;
// 	/* Whether the content is replied message, so it should increase bottom padding */
// 	isReplied?: boolean;
// 	isReply?: boolean;
// 	isSending?: boolean;
// 	deleted?: boolean;
// 	onClick?: MouseEventHandler;
// 	groupPosition?: MessageGroupPosition;
// }

type MessageContentProps = MessageCommonProps & {
	isReplied?: boolean;
	isReply?: boolean;
	status?: boolean;
};

export const MessageContent = ({
	id,
	content,
	isAuthor,
	isReplied = false,
	isReply = false,
	isSending = false,
	setFocusedMessageId,
	isFocused,
	reactions,
	status,
	isBot,
	position,
	onClick,
	className,
	isUserStatsOpen,
}: MessageContentProps) => {
	const {
		user: { id: userId, displayName: currentUserDisplayName },
	} = useAuth();
	const { openReactionsSheet, members } = useContext(ChatContainerContext);
	const { mutateAsync } = useCreateMessageReactionMutation();
	const { enqueueSnackbar } = useSnackbar();
	const fireOpenReactionsEvent = useAnalyticsEvent('Chat_Open_Reactions');
	const fireSendReactionEvent = useAnalyticsEvent('Chat_Send_Reaction');
	const fireErrorEvent = useAnalyticsEvent('Error');
	const isTablet = useMediaQuery(`(min-width: ${constants.sizeTablet}`);

	const longPressEvent = useLongPress(
		(e) => {
			if ((e.target as HTMLElement).tagName !== 'A') {
				setFocusedMessageId(id);
				fireOpenReactionsEvent();
				Haptics.impact({ style: ImpactStyle.Medium });
			}
		},
		(e) => {
			const target = e.target as HTMLElement;
			if (target?.tagName !== 'A') {
				setFocusedMessageId(undefined);
				onClick && onClick(id);
			} else {
				const href = target.getAttribute('href');
				if (href) {
					window.open(href, '_blank');
				}
			}
		},
		{
			isPreventDefault: true,
			delay: 300,
		},
	);

	const userReaction = reactions?.find((r) => r.userId === userId);

	function tokenizeContent(text: string): React.ReactNode[] {
		const escapedNames = [...(members ?? []).map((m) => m.displayName ?? ''), currentUserDisplayName ?? '']
			.filter(Boolean)
			.sort((a, b) => b.length - a.length)
			.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
		const mentionPattern =
			escapedNames.length > 0
				? `@(?:${escapedNames.join('|')})|@\\S+`
				: `@\\S+`;
		const splitRegex = new RegExp(`(${mentionPattern})`, 'g');
		const matchRegex = new RegExp(`^(?:${mentionPattern})$`);
		const segments = text.split(splitRegex);
		return segments.map((segment, index) => {
			if (matchRegex.test(segment)) {
				const isOwn = segment.slice(1) === currentUserDisplayName;
				return (
					<span
						key={index}
						className={
							isOwn
								? styles['content__text__mention--own']
								: styles['content__text__mention']
						}
					>
						{segment}
					</span>
				);
			}
			return segment ? (
				<Linkify key={index} options={{ target: '_blank' }}>
					{segment}
				</Linkify>
			) : null;
		});
	}

	return (
		<div
			className={classNames([
				styles['wrapper'],
				isAuthor ? styles['sent'] : styles['received'],
				isFocused && styles['focused'],
				className,
			])}
		>
			<ReactionPanel
				className={classNames([
					styles['reaction-panel'],
					isFocused && styles['focused'],
				])}
				selectedEmoji={userReaction?.emojiCode}
				onClick={(code) => {
					Haptics.impact({ style: ImpactStyle.Light });
					setFocusedMessageId(undefined);
					mutateAsync({
						messageId: id,
						emojiCode: code,
					})
						.then((res) => {
							fireSendReactionEvent({
								emojiCode: code,
							});
						})
						.catch((err) => {
							console.error(err);
							fireErrorEvent({
								name: 'SendReactionError',
								details: err,
							});
							enqueueSnackbar('Your reaction was not sent', {
								variant: 'warning',
							});
						});
				}}
			/>
			<div
				className={classNames([
					styles['content'],
					isSending && styles['content--sending'],
					isAuthor
						? styles['content--sent']
						: styles['content--received'],
					isBot && styles['content--bot'],
					status && styles['status'],
					isReply && styles['content--reply'],
					isReplied && styles['content--replied'],
					position && styles[position],
					isTablet && styles['hover'],
					isUserStatsOpen && styles['active'],
				])}
				{...longPressEvent}
			>
				<div
					className={classNames([
						styles['content__text'],
						isReplied && styles['content__text--replied'],
					])}
				>
					{tokenizeContent(content)}
				</div>
			</div>
			{!!reactions?.length && (
				<MessageReaction
					onClick={() => openReactionsSheet(reactions)}
					values={reactions.map((r) => r.emojiCode)}
				/>
			)}
		</div>
	);
};
